'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';

const ENTRY_KEY = 'companyPreviewEntry';

/** Shallow preview state: Back closes the first open, switches replace it. */
export function useCompanyPreview() {
  const searchParams = useSearchParams();
  const previewEuId = searchParams.get('preview') || null;
  const pushedEntry = useRef<string | null>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);
  const closing = useRef(false);

  useEffect(() => {
    if (!previewEuId && wasOpen.current) {
      if (returnFocus.current?.isConnected) {
        returnFocus.current.focus({ preventScroll: true });
      }
      returnFocus.current = null;
    }
    wasOpen.current = !!previewEuId;
    closing.current = false;
  }, [previewEuId]);

  const openPreview = useCallback((euId: string, trigger?: HTMLElement) => {
    const url = new URL(window.location.href);
    returnFocus.current =
      trigger ??
      (document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null);
    if (!url.searchParams.get('preview')) {
      const entry = crypto.randomUUID();
      pushedEntry.current = entry;
      url.searchParams.set('preview', euId);
      // Next merges its internal state; forwarding it skips search-param sync.
      window.history.pushState({ [ENTRY_KEY]: entry }, '', url);
    } else {
      url.searchParams.set('preview', euId);
      window.history.replaceState(
        { [ENTRY_KEY]: window.history.state?.[ENTRY_KEY] },
        '',
        url,
      );
    }
  }, []);

  const closePreview = useCallback(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('preview') || closing.current) return;
    closing.current = true;
    if (
      pushedEntry.current &&
      window.history.state?.[ENTRY_KEY] === pushedEntry.current
    ) {
      window.history.back();
    } else {
      url.searchParams.delete('preview');
      window.history.replaceState(
        { [ENTRY_KEY]: window.history.state?.[ENTRY_KEY] },
        '',
        url,
      );
    }
  }, []);

  return { previewEuId, openPreview, closePreview };
}
